import {Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output} from '@angular/core';
import {ControlValueAccessor, FormBuilder, FormControl, NG_VALUE_ACCESSOR} from "@angular/forms";
import {ReplaySubject} from "rxjs";
import {InsuranceCompany} from "../../interface/insurance-company";
import {IC_PZU, InsuranceCompanyService} from "../../services/insurance-company.service";
import {IddTrainingTrainerService} from "../../services/idd-training-trainer.service";
import {debounceTime, delay, distinctUntilChanged, first, startWith, takeUntil, tap} from "rxjs/operators";
import {CountedAndPaginated} from "../../interface/response/counted-and-paginated";
import {IddTrainingTrainer} from "../../interface/idd-training-trainer";
import {MatSelectChange} from "@angular/material/select";
import {Dictionary} from "../../interface/dictionary";
import {ListResponse} from "../../interface/response/list-response";
import {User} from "../../interface/user";
import {Ofwca} from "../../interface/ofwca";
import {Contract} from "../../interface/contract";
import {RouterStateUrl} from "../../ngrx/route-serializer";

@Component({
  selector: 'app-dict-idd-training-trainer',
  templateUrl: './dict-idd-training-trainer.component.html',
  styleUrls: ['./dict-idd-training-trainer.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, multi: true, useExisting: forwardRef(() => DictIddTrainingTrainerComponent)}
  ]
})
export class DictIddTrainingTrainerComponent implements OnInit, ControlValueAccessor, OnDestroy {
  @Input() label: string;
  @Input() multiple = false;
  @Input() control: FormControl = null;
  @Input() required = false;
  @Input() companyId?: ReplaySubject<number>;
  @Input() iddtrainingtrainerId?: ReplaySubject<number>;
  @Input() hideBlocked = false;
  @Input() hideInactive = false;
  @Input() hideChildren = false;
  @Input() forceShowPzu = false;
  @Input() onlyKeys?: string[] = null;
  @Input() showElements = 5;
  @Input() forceShowInsuranceCompanyIds: number[];
  @Output() selectedIddTrainingTrainer: EventEmitter<IddTrainingTrainer> = new EventEmitter<IddTrainingTrainer>();
  @Output() selectedIddTrainingTrainers: EventEmitter<IddTrainingTrainer[]> = new EventEmitter<IddTrainingTrainer[]>();
  externalControl = true;
  isFirstName = true;
  disabled = false;
  items: IddTrainingTrainer[] = [];
  filteredItems: IddTrainingTrainer[] = [];
  value?: string = null;
  filterControl: FormControl;
  itemsSubject: ReplaySubject<ListResponse<IddTrainingTrainer>> = new ReplaySubject<ListResponse<IddTrainingTrainer>>(1);

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private iddTrainingTrainerService: IddTrainingTrainerService,
    private fb: FormBuilder
  ) {
    this.loadItems();
    this.filterControl = fb.control('');
  }

  ngOnInit(): void {
    if (this.control === null) {
      this.control = new FormControl('');
      this.externalControl = false;
    }

    this.control.valueChanges
      .pipe(
        startWith(this.control.value),
        takeUntil(this.destroyed$)
      )
      .subscribe((val: string) => {
        if (val) {
          let exists = false;
          this.items.forEach((iddTrainingTrainer: IddTrainingTrainer) => {
            if (iddTrainingTrainer.uuid === val) {
              exists = true;
            }
          });

          if (!exists) {
            this.iddTrainingTrainerService.loadNotCountedList(0, this.showElements, {
              uuid: val
            }, this.itemsSubject);
          }
        }
      });

    this.filterControl.valueChanges
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        takeUntil(this.destroyed$)
      )
      .subscribe((val) => {
        if (val) {
          this.iddTrainingTrainerService.loadNotCountedList(0, this.showElements, {
            context: val,
            active: true,
          }, this.itemsSubject);
        } else if (this.control.value) {
          this.iddTrainingTrainerService.loadNotCountedList(0, this.showElements, {
            uuid: this.control.value
          }, this.itemsSubject);
        }
      });

    this.itemsSubject
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: ListResponse<IddTrainingTrainer>) => {
        this.items = result.items;
      });
  }

  loadItems(): void {
    this.iddTrainingTrainerService.getIddTrainingTrainerListSubject()
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<IddTrainingTrainer>) => {
          this.items = e.items;
        })
      ).subscribe((result: CountedAndPaginated<IddTrainingTrainer>) => {
      this.items = result.items
        .filter(trainer => trainer.active)
        .sort((a, b) => a.firstName.localeCompare(b.firstName));
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }


  setDisabledState(isDisabled: boolean): void {
    if (this.externalControl) {
      return;
    }

    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }

    this.disabled = isDisabled;
  }

  writeValue(value: any): void {
    if (this.externalControl) {
      return;
    }

    this.control.setValue(value);
    this.value = value;
  }

  onSelectionChange(e: MatSelectChange): void {
    this.items.forEach((iddTrainingTrainer: IddTrainingTrainer) => {
      if (iddTrainingTrainer.uuid === e.value) {
        this.selectedIddTrainingTrainer.emit(iddTrainingTrainer);
      }
    });

    this.value = e.value;
    this.onChange(this.value);
  }

  clearValue(): void {
    this.control.setValue(this.multiple ? [] : '');
    this.writeValue(this.multiple ? [] : '');
    this.onChange(this.value);
  }

  onChange: any = () => { };
  onTouched: any = () => { };

}


